import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useTranslation } from '@shared/core';

export type CardSizeLabels = Readonly<{
  cocktail: string;
  luncheon: string;
  postcard4x6: string;
}>;

// Untranslated fallback for callers outside a React/i18n context (and tests).
const DEFAULT_LABELS: CardSizeLabels = {
  cocktail: 'Cocktail',
  luncheon: 'Luncheon',
  postcard4x6: 'Postcard 4x6',
};

// Translated labels for named card sizes. The tq calls live here — directly in a
// hook with `useTranslation` in scope — so translation extraction can trace them;
// do not move them into `formatCardSizeName` (the extractor cannot follow
// function parameters).
export const useCardSizeLabels = (): CardSizeLabels => {
  const { tq } = useTranslation('stationery');
  return {
    cocktail: tq`Cocktail`,
    luncheon: tq`Luncheon`,
    postcard4x6: tq`Postcard 4x6`,
  };
};

// 4x6 thank-you cards are postcards; the label is orientation-agnostic because a
// 6x4 landscape card is the same physical postcard rotated (orientation has its
// own picker).
export const isPostcardCardSize = (widthInches: number, heightInches: number): boolean => (widthInches === 4 && heightInches === 6) || (widthInches === 6 && heightInches === 4);

/**
 * Customer-facing name for a card size. Named sizes (napkins, postcards) read as
 * products; everything else falls back to measurements. Pass labels from
 * `useCardSizeLabels()` where a React context is available.
 */
export const formatCardSizeName = (
  category: StationeryTemplateCategoryEnum | undefined,
  widthInches: number,
  heightInches: number,
  labels: CardSizeLabels = DEFAULT_LABELS,
): string => {
  if (category === StationeryTemplateCategoryEnum.napkin) {
    if (widthInches === 4.75) return labels.cocktail;
    if (widthInches === 6.5) return labels.luncheon;
  }
  if (category === StationeryTemplateCategoryEnum.thankYou && isPostcardCardSize(widthInches, heightInches)) {
    return labels.postcard4x6;
  }
  return `${widthInches} × ${heightInches} inches`;
};
